import React, { useCallback, useState } from 'react';
import LoadingSpinner from 'components/LoadingSpinner';
import useLinkedRecords from './hooks/useLinkedRecords';
import LinkedRecordsTable from './components/LinkedRecordsTable';
import RecommendationLinkedRecordDrawer from './components/RecommendationLinkedRecordDrawer';
import { LINKED_RECORDS_PAGE_SIZE } from './constants';
import { LinkedRecordRow } from './types';

interface LinkedRecordsSectionProps {
  recommendationId: string;
}

/**
 * 36px rhythm, per the design. No `pt-4` on the heading: it stacks on the
 * divider's bottom margin and pushes the divider off center, which the other
 * sections on this page — which do carry it below their own dividers — still do.
 * The preceding section's smaller bottom margin collapses into the divider's top
 * margin, so the gap above it is 36px rather than the sum of the two.
 */
const SectionShell = ({ children }: { children: React.ReactNode }) => (
  <div data-testid="linked-records-section" className="mb-6">
    <hr data-testid="linked-records-divider" className="my-36px" />
    <div className="text-2xl font-bold mb-36px">Linked Records</div>
    {children}
  </div>
);

const LinkedRecordsSection = ({
  recommendationId
}: LinkedRecordsSectionProps) => {
  const [page, setPage] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState<LinkedRecordRow | null>(
    null
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { rows, totalRecords, isLoading, hasError } = useLinkedRecords(
    recommendationId,
    page,
    LINKED_RECORDS_PAGE_SIZE
  );

  const handleViewMore = useCallback((record: LinkedRecordRow) => {
    setSelectedRecord(record);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  if (isLoading) {
    return (
      <SectionShell>
        <div
          data-testid="linked-records-loading"
          className="flex items-center justify-center py-8"
        >
          <LoadingSpinner message="" />
        </div>
      </SectionShell>
    );
  }

  // Surface the failure rather than letting it fall through to the empty case
  // below, which would hide the section and read as "no linked records".
  if (hasError) {
    return (
      <SectionShell>
        <div
          role="alert"
          data-testid="linked-records-error"
          className="flex items-center justify-center py-8 text-neutral-50"
        >
          Unable to load linked records.
        </div>
      </SectionShell>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  return (
    <SectionShell>
      <LinkedRecordsTable
        records={rows}
        totalRecords={totalRecords}
        page={page}
        onPageChange={setPage}
        onViewMore={handleViewMore}
      />
      <RecommendationLinkedRecordDrawer
        open={isDrawerOpen}
        record={selectedRecord}
        onClose={handleCloseDrawer}
        recommendationId={recommendationId}
      />
    </SectionShell>
  );
};

export default LinkedRecordsSection;
